import { useLayoutEffect, useRef, useState } from "react";
import { FACE, LEVEL_LINE, SIDE_EDGE, STREET, WATER, heightLabel } from "@/lib/terrain";

export const LEGEND_H = 34;
export const LEGEND_GAP = 8;

// A tiny block drawn like a board square: top face, right and bottom side faces, level lines.
function MiniBlock({ h, top = 16, step = 2.4 }) {
  const d = step * h;
  const W = top + d;
  const col = FACE[h];
  const ts = Array.from({ length: h }, (_, j) => (j + 1) * step - 0.4);
  // Footprint origin at (d, d): the top face sits at (0, 0).
  return (
    <svg width={W + 1} height={W + 1} viewBox={`${-d - 0.5} ${-d - 0.5} ${W + 1} ${W + 1}`} aria-hidden="true" style={{ display: "block" }}>
      <polygon points={`${top - d},${-d} ${top},0 ${top},${top} ${top - d},${top - d}`} fill={col.right} />
      <polygon points={`${-d},${top - d} ${top - d},${top - d} ${top},${top} 0,${top}`} fill={col.bottom} />
      {ts.map((t) => (
        <g key={t}>
          <line x1={top - d + t} y1={-d + t} x2={top - d + t} y2={top - d + t} stroke={LEVEL_LINE} strokeWidth={0.8} />
          <line x1={-d + t} y1={top - d + t} x2={top - d + t} y2={top - d + t} stroke={LEVEL_LINE} strokeWidth={0.8} />
        </g>
      ))}
      <polyline points={`${top - d},${-d} ${top},0 ${top},${top} 0,${top} ${-d},${top - d}`} fill="none" stroke={SIDE_EDGE} strokeWidth={0.8} />
      <rect x={-d} y={-d} width={top} height={top} fill={col.top} stroke="rgba(42,46,44,0.3)" strokeWidth={0.6} />
    </svg>
  );
}

// A tiny building square: half road, sidewalk (or green verge with trees), plot.
function MiniStreet({ green }) {
  return (
    <svg width={SW} height={SW} viewBox="0 0 100 100" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      <rect width="100" height="100" fill={STREET.road} />
      <rect x="18" y="18" width="64" height="64" fill={green ? STREET.verge : STREET.sidewalk} />
      <rect x="34" y="34" width="32" height="32" fill={STREET.plot} />
      {green && <rect x="32" y="32" width="36" height="36" fill="none" stroke={STREET.swale} strokeWidth="5" />}
      {green && [30, 50, 70].map((p) => <circle key={p} cx={p} cy="25" r="7" fill={STREET.tree} />)}
    </svg>
  );
}

const SW = 16; // swatch size
const swatch = { width: SW, height: SW, flexShrink: 0, borderRadius: 2 };
const text = { fontSize: 11, fontWeight: 500, color: "var(--ink-3)", whiteSpace: "nowrap" };

// 3D BOARD (R6): one 34 px row under the board: the four heights, then the three water markings.
// Shrinks (proportions kept) when the centre column is narrower than the row.
export default function BoardLegend({ maxWidth }) {
  const ref = useRef(null);
  const [k, setK] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !maxWidth) return;
    const natural = el.scrollWidth;
    const next = natural > maxWidth ? Math.max(0.6, (maxWidth - 2) / natural) : 1;
    setK((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
  }, [maxWidth]);
  return (
    <div data-testid="board-legend-slot" className="shrink-0 flex justify-center" style={{ height: LEGEND_H, marginTop: LEGEND_GAP, width: "100%", overflow: "visible" }}>
    <div
      ref={ref}
      data-testid="board-legend"
      data-scale={k.toFixed(3)}
      className="shrink-0 flex items-center"
      style={{ height: LEGEND_H, gap: 10, padding: "0 2px", width: "max-content", transform: k < 1 ? `scale(${k})` : undefined, transformOrigin: "50% 50%" }}
    >
      <span className="section-label" style={{ fontSize: 11 }}>Height</span>
      <span className="flex items-center" style={{ gap: 7 }}>
        {[1, 2, 3, 4].map((h) => (
          <span key={h} data-testid={`legend-height-${h}`} className="flex items-center" style={{ gap: 4 }}>
            <MiniBlock h={h} />
            <span className="font-coord" style={{ fontSize: 10, color: "var(--ink-3)" }}>{heightLabel(h)}</span>
          </span>
        ))}
      </span>
      <span aria-hidden="true" style={{ width: 1, height: 20, background: "var(--line)" }} />
      <span data-testid="legend-water" className="flex items-center" style={{ gap: 5 }}>
        <span style={{ ...swatch, background: `linear-gradient(${WATER.overlay}, ${WATER.overlay}), ${FACE[1].top}`, boxShadow: `1.5px 1.5px 0 ${WATER.right}` }} />
        <span style={text}>Under water</span>
      </span>
      <span data-testid="legend-held-back" className="flex items-center" style={{ gap: 5 }}>
        <span style={{ ...swatch, background: `repeating-linear-gradient(135deg, rgba(36,73,90,0.32) 0 2px, transparent 2px 6px), ${FACE[1].top}`, boxShadow: "inset 0 0 0 1px rgba(36,73,90,0.4)" }} />
        <span style={text}>Held back by walls</span>
      </span>
      <span aria-hidden="true" style={{ width: 1, height: 20, background: "var(--line)" }} />
      <span data-testid="legend-street" className="flex items-center" style={{ gap: 5 }}>
        <MiniStreet />
        <span style={text}>Street</span>
      </span>
      <span data-testid="legend-green-street" className="flex items-center" style={{ gap: 5 }}>
        <MiniStreet green />
        <span style={text}>Green Street</span>
      </span>
      <span aria-hidden="true" style={{ width: 1, height: 20, background: "var(--line)" }} />
      <span data-testid="legend-next-rise" className="flex items-center" style={{ gap: 5 }}>
        <span style={{ ...swatch, background: FACE[2].top, border: "2px dashed #24495A" }} />
        <span style={text}>Building floods at next rise</span>
      </span>
    </div>
    </div>
  );
}
